import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  Band,
  BookedWorkCell,
  GroupShell,
  LedgerHeader,
  Money,
  type LedgerCollapse,
  type LedgerColumn,
} from '../../../shared/components/ledger';
import { ACTIVITY, weekTotal } from './data';
import { renderBookedRow } from './bookedRow';

export const ACTIVITY_COLUMNS: LedgerColumn[] = [
  { key: BookedWorkCell.Ref, label: 'Ref', flex: 0.8 },
  { key: BookedWorkCell.SitePlot, label: 'Site / Plot', flex: 1.9 },
  { key: BookedWorkCell.BookedBy, label: 'Booked by', flex: 1.1 },
  { key: BookedWorkCell.When, label: 'When', flex: 1 },
];

interface ActivityLedgerDemoProps {
  collapse: LedgerCollapse;
  onOpen: (label: string) => void;
}

/**
 * Activity drives GroupShell in controlled mode: it tracks only the weeks the
 * user explicitly collapsed, so weeks arriving from the next page stay open.
 */
export function ActivityLedgerDemo({
  collapse,
  onOpen,
}: ActivityLedgerDemoProps) {
  const [collapsedWeeks, setCollapsedWeeks] = useState<ReadonlySet<string>>(
    new Set()
  );
  const ctx = {
    columns: ACTIVITY_COLUMNS,
    collapse,
    onOpen,
    withSitePlot: true,
  };

  const toggleWeek = (label: string) => {
    setCollapsedWeeks(current => {
      const next = new Set(current);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const shown = ACTIVITY.reduce((sum, week) => sum + week.rows.length, 0);

  return (
    <Box>
      <LedgerHeader columns={ACTIVITY_COLUMNS} collapse={collapse} />
      {ACTIVITY.map(week => {
        const [weekName, weekRange] = week.label.split(' · ');
        return (
          <GroupShell
            key={week.label}
            open={!collapsedWeeks.has(week.label)}
            onToggle={() => toggleWeek(week.label)}
            header={(open, toggle) => (
              <Band
                collapse={collapse}
                primary={weekName}
                secondary={weekRange}
                count={week.rows.length}
                open={open}
                onToggle={toggle}
                trailing={<Money value={weekTotal(week)} />}
              />
            )}
          >
            {week.rows.map(row => renderBookedRow(row, ctx))}
          </GroupShell>
        );
      })}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          py: 1.5,
        }}
      >
        <Button size="small" color="inherit" sx={{ color: 'text.secondary' }}>
          Load more
        </Button>
        <Typography variant="caption" color="text.secondary">
          {shown} of 128 bookings
        </Typography>
      </Box>
    </Box>
  );
}
